import { PacienteDoPedido } from "./PacienteDoPedido";
import { ChevronRight } from "lucide-react";
import { formatDate } from "./prosthesis-meta";
import { DENTIST_STATUS_META, dentistStatusBucket } from "./dentist-status";
import type { ProsthesisOrderRow } from "./types";
import { rotuloDoMaterialDoServico } from "@/components/prosthesis/rotulo-do-material";

type Props = {
  orders: ProsthesisOrderRow[];
  onOpen: (id: string) => void;
};

/** Serviço · Material · Cor de cada serviço do pedido, numa string só. Na lista o interesse é
 *  reconhecer o pedido de relance, não auditar item por item — isso é papel do detalhe. */
function resumoDosServicos(order: ProsthesisOrderRow): string {
  const partes = (order.services ?? []).map((s) =>
    [
      s.serviceCategory?.label,
      rotuloDoMaterialDoServico(s),
      s.shadeOption?.code,
    ]
      .filter(Boolean)
      .join(" · "),
  );
  return partes.filter(Boolean).join(" • ");
}

/**
 * Modo Lista no celular.
 *
 * A tabela do desktop tem sete colunas e, espremida em 360px, quebrava o nome do paciente em várias
 * linhas de uma palavra e encostava os serviços na borda. Aqui cada pedido é uma linha de três
 * faixas — identidade, serviços, situação — mais densa que o cartão e ainda legível.
 *
 * A tabela original continua intacta no desktop e no tablet: este componente só é montado abaixo
 * de `md`, pelo próprio `ProsthesisOrdersTable`.
 */
export function ProsthesisOrdersMobileList({ orders, onOpen }: Props) {
  return (
    <ul className="divide-y divide-zinc-100 overflow-hidden rounded-lg border border-zinc-200 bg-white dark:divide-border/50 dark:border-border dark:bg-card">
      {orders.map((o) => {
        const bucket = dentistStatusBucket(o.status);
        const meta = DENTIST_STATUS_META[bucket];
        const servicos = resumoDosServicos(o);
        const prazo = o.deadline ? formatDate(o.deadline) : null;

        return (
          <li key={o.id}>
            <button
              type="button"
              onClick={() => onOpen(o.id)}
              className="flex min-h-11 w-full items-start gap-2 px-3 py-3 text-left transition-colors hover:bg-zinc-50 dark:hover:bg-muted/50"
            >
              <span className="min-w-0 flex-1">
                {/* Nome em até duas linhas: em 320px "Paciente UAT Bloco3 Caso35" não cabe numa só,
                    e cortar o nome do paciente é justamente o que não se pode fazer. */}
                <span className="block break-words text-sm font-medium leading-snug text-zinc-900 [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] overflow-hidden dark:text-foreground">
                  <PacienteDoPedido order={o} />
                </span>

                {servicos && (
                  <span className="mt-0.5 block break-words text-xs leading-snug text-zinc-500 [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] overflow-hidden dark:text-muted-foreground">
                    {servicos}
                  </span>
                )}

                <span className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span
                    className="inline-flex shrink-0 rounded-full px-2 py-0.5 text-[10.5px] font-bold uppercase"
                    style={{ backgroundColor: meta.bg, color: meta.color }}
                  >
                    {meta.label}
                  </span>
                  <span className="text-[11px] tabular-nums text-zinc-500 dark:text-muted-foreground">
                    {prazo ? `Prazo ${prazo}` : formatDate(o.createdAt)}
                  </span>
                </span>
              </span>

              {/* Sinaliza que a linha abre o detalhe — sem isso, a única pista era o hover, que não
                  existe no toque. */}
              <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-zinc-300 dark:text-muted-foreground/60" />
            </button>
          </li>
        );
      })}
    </ul>
  );
}
